import type { FC } from 'react';
import { lazy, Suspense } from 'react';

import { useBlockStrayFileDrops } from 'hooks/useBlockStrayFileDrops';

/* ErrorBoundary is eagerly imported so it is available even when lazy chunks
   fail to load (e.g. network outage, deploy mismatch). It only uses the static
   classes in public/index.html — no theme tokens — so it is safe to render
   outside ThemeProvider. */
import ErrorBoundary from 'containers/common/ErrorBoundary';

const Providers = lazy(() => import('containers/common/Providers'));
const Content = lazy(() => import('containers/common/Content'));
const ToastsContainer = lazy(() => import('containers/common/ToastsContainer'));

/* Plain fallback that renders before ThemeProvider is available — must NOT use
   theme tokens. Styled via the static classes in public/index.html (which mirror
   OS `prefers-color-scheme` directly through a plain media query) rather than
   inline styles, so this spinner is dark-mode aware before React theme context
   even exists. */
const AppFallback: FC = () => (
  <div className="app-fallback-container">
    <div className="app-fallback-spinner" />
  </div>
);

const App: FC = () => {
  useBlockStrayFileDrops();

  return (
    <ErrorBoundary>
      <Suspense fallback={<AppFallback />}>
        <Providers>
          <Content />
          <ToastsContainer />
        </Providers>
      </Suspense>
    </ErrorBoundary>
  );
};

export default App;
